// @flow

/**
 * Common enumerations used across TrimbleMaps.
 */

export const Common = {
    /**
     * Enumeration for map style values.
     *
     * @param {string} TRANSPORTATION
     * @param {string} SATELLITE
     * @param {string} TERRAIN
     * @param {string} BASIC
     * @param {string} BASIC_DARK
     * @param {string} DATALIGHT
     * @param {string} DATADARK
     * @param {string} TRANSPORTATION_DARK
     * @param {string} ACCESSIBLE_LIGHT
     * @param {string} ACCESSIBLE_DARK
     * @param {string} MOBILE_DAY
     * @param {string} MOBILE_NIGHT
     * @param {string} MOBILE_SATELLITE
     * @param {string} SIMPLE_LIGHT
     * @param {string} SIMPLE_DARK
     *
     * @example
     * var map = new TrimbleMaps.Map({
     *   container: 'map',
     *   center: [-122.420679, 37.772537],
     *   zoom: 13,
     *   style: TrimbleMaps.Common.Style.TRANSPORTATION
     * });
     *
     */
    Style: {
        DEFAULT: 'default',
        TRANSPORTATION: "transportation",
        SATELLITE: 'satellite',
        TERRAIN: 'terrain',
        BASIC: 'basic',
        BASIC_DARK: 'basic_dark',
        DATALIGHT: 'datalight',
        DATADARK: 'datadark',
        TRANSPORTATION_DARK: 'transportation_dark',
        ACCESSIBLE_LIGHT: 'light_accessible',
        ACCESSIBLE_DARK: 'dark_accessible',
        MOBILE_DAY: 'mobileday',
        MOBILE_NIGHT: 'mobilenight',
        MOBILE_SATELLITE: 'mobilesatellite',
        SIMPLE_LIGHT: 'simple_light',
        SIMPLE_DARK: 'simple_dark'
    },
    /**
     * Enumeration for map regions values.
     *
     * @property {string} AF Africa
     * @property {string} AS Asia
     * @property {string} EU Europe
     * @property {string} ME Middle East
     * @property {string} NA North America
     * @property {string} OC Australia
     * @property {string} SA South America
     * @property {string} WW Worldwide
     *
     * @example
     * map.setRegion(TrimbleMaps.Common.Region.NA);
     */
    Region: {
        AF: 'af',
        AS: 'as',
        EU: 'eu',
        ME: 'me',
        NA: 'na',
        OC: 'oc',
        SA: 'sa',
        WW: 'ww'
    },
    /**
     * Enumeration for the routing vehicle type.
     *
     * @property {number} TRUCK Truck
     * @property {number} LIGHT_TRUCK Light Truck
     * @property {number} AUTOMOBILE Automobile
     *
     */
    VehicleType: {
        TRUCK: 0,
        LIGHT_TRUCK: 1,
        AUTOMOBILE: 2
    },
    /**
     * Enumeration for the route type.
     *
     * @property {number} PRACTICAL Practical
     * @property {number} SHORTEST Shortest
     * @property {number} FASTEST Fastest
     *
     */
    RouteType: {
        PRACTICAL: 0,
        SHORTEST: 1,
        FASTEST: 2
    },
    /**
     * Enumeration for the routing optimization.
     *
     * @property {number} NONE No optimization
     * @property {number} OPTIMIZE_ALL_STOPS Optimize all stops
     * @property {number} OPTIMIZE_INTERMEDIATE_STOPS Optimize only stops
     *
     */
    RouteOptimization: {
        NONE: 0,
        OPTIMIZE_ALL_STOPS: 1,
        OPTIMIZE_INTERMEDIATE_STOPS: 2
    },
    /**
     * Enumeration for the Class override.
     *
     * @property {number} NONE No class overrides
     * @property {number} FIFTY_THREE 53' class override
     * @property {number} NATIONAL_NETWORK National Network class override
     * @property {number} ALL 53' and National Network class override
     *
     */
    ClassOverride: {
        NONE: 0,
        FIFTY_THREE: 1,
        NATIONAL_NETWORK: 2,
        ALL: 3
    },
    /**
     * Enumeration for the routing HazMat type.
     *
     * @property {number} NONE None
     * @property {number} GENERAL General
     * @property {number} CAUSTIC Caustic
     * @property {number} EXPLOSIVES Explosives
     * @property {number} FLAMMABLE Flammable
     * @property {number} INHALANTS Inhalants
     * @property {number} RADIOACTIVE Radioactive
     * @property {number} HARMFUL_TO_WATER HarmfulToWater
     * @property {number} TUNNEL Tunnel
     *
     */
    HazMatType: {
        NONE: 0,
        GENERAL: 1,
        CAUSTIC: 2,
        EXPLOSIVES: 3,
        FLAMMABLE: 4,
        INHALANTS: 5,
        RADIOACTIVE: 6,
        HARMFUL_TO_WATER: 7,
        TUNNEL: 8
    },
    /**
     * Enumeration for the map units.
     *
     * @property {number} ENGLISH English
     * @property {number} METRIC Metric
     *
     */
    Unit: {
        ENGLISH: 0,
        METRIC: 1
    },
    /**
     * Enumeration for the routing distance units.
     *
     * @property {number} MILES Miles
     * @property {number} KILOMETERS Kilometers
     *
     */
    DistanceUnit: {
        MILES: 0,
        KILOMETERS: 1
    },
    /**
     * Enumeration for the routing truck units.
     *
     * @property {number} ENGLISH English
     * @property {number} METRIC Metric
     *
     */
    TruckUnit: {
        ENGLISH: 0,
        METRIC: 1
    },
    /**
     * Enumeration for the routing fuel units.
     *
     * @property {number} GALLONS Gallons
     * @property {number} LITERS Liters
     *
     */
    FuelUnit: {
        GALLONS: 0,
        LITERS: 1
    },
    /**
     * Represents vehicle dimensions of a truck trailer to use for.
     * @property {number} NONE Indicates that no particular truck style is chosen
     * @property {number} TWENTY_EIGHT Indicates that a 28' double trailer should be used
     * @property {number} FORTY Indicates that a Heavy Duty Straight trailer should be used
     * @property {number} FORTY_EIGHT Indicates that a Heavy Duty Semitrailer (48' x 96") should be used
     * @property {number} FIFTY_THREE Indicates that a Heavy Duty Semitrailer should be used
     * @property {number} FULL_SIZE_VAN Indicates a full-size van
     * @property {number} TWENTY_SIX Indicates a Medium Duty Straight trailer
     * @property {number} CONVENTIONAL_SCHOOL_BUS Indicates a conventional school bus
     * @property {number} SMALL_SCHOOL_BUS Indicates a small school bus
     *
     */
    TruckConfig:{
        NONE: 0,
        TWENTY_EIGHT: 1,
        FORTY: 2,
        FORTY_EIGHT: 3,
        FIFTY_THREE: 4,
        FULL_SIZE_VAN: 5,
        TWENTY_SIX_STRAIGHT_TRUCK: 6,
        CONVENTIONAL_SCHOOL_BUS: 7,
        SMALL_SCHOOL_BUS: 8
    },
    /**
     * Enumeration for the routing currency units.
     *
     * @property {number} USD US dollars
     * @property {number} CAD Canadian dollars
     * @property {number} ALL Albanian lek
     * @property {number} AMD Aremenian dram
     * @property {number} AZN Azerbaijani manat
     * @property {number} BAM Bosnia and Herzegovina convertible mark
     * @property {number} BGN Bulgarian lev
     * @property {number} BYR Belarusian ruble
     * @property {number} CHF Swiss franc
     * @property {number} CZK Czech koruna
     * @property {number} DKK Danish krone
     * @property {number} EUR Euros
     * @property {number} GBP Pound sterling
     * @property {number} GEL Georgian lari
     * @property {number} HRK Croatian kuna
     * @property {number} HUF Hungarian forint
     * @property {number} ISK Icelandic króna: second króna
     * @property {number} MDL Moldovan leu
     * @property {number} MKD Second Macedonian denar
     * @property {number} NOK Norwegian krone
     * @property {number} PLN Polish złoty
     * @property {number} RON Fourth Romanian leu
     * @property {number} RSD Serbian dinar
     * @property {number} RUB Russian ruble
     * @property {number} SEK Swedish krona
     * @property {number} TRY Turkish lira
     * @property {number} UAH Ukrainian hryvnia
     *
     */
    TollCurrency: {
        USD: 0,
        CAD: 1,
        ALL: 2,
        AMD: 3,
        AZN: 4,
        BAM: 5,
        BGN: 6,
        BYR: 7,
        CHF: 8,
        CZK: 9,
        DKK: 10,
        EUR: 11,
        GBP: 12,
        GEL: 13,
        HRK: 14,
        HUF: 15,
        ISK: 16,
        MDL: 17,
        MKD: 18,
        NOK: 19,
        PLN: 20,
        RON: 21,
        RSD: 22,
        RUB: 23,
        SEK: 24,
        TRY: 25,
        UAH: 26
    },
    /**
     * Enumeration for the data version.
     *
     * @property {string} CURRENT Current
     * @property {string} EU EU
     * @property {string} OC OC
     * @property {string} SA SA
     * @property {string} NA NA
     * @property {string} AF AF
     * @property {string} AS AS
     * @property {string} ME ME
     * @property {string} JP JP
     *
     */
    DataVersion: {
        // GT
        // WW
        // MX,
        // 18- 27 will not be supported
        // PCM18, ..., PCM35 ...
        // From our meeting, we will not add historical versions or remove reginal versions. Instead, we will add documentation and sample code for getting historical reports.
        CURRENT: 'Current',
        EU: 'PCM_EU',
        OC: 'PCM_OC',
        SA: 'PCM_SA',
        NA: 'PCM_NA',
        AF: 'PCM_AF',
        AS: 'PCM_AS',
        ME: 'PCM_ME',
        IN: 'PCM_IN', // PCM does not have this version. Remove from API documentaion.
        SE: 'PCM_SE', // PCM does not have this version. Remove from API documentaion.
        JP: 'PCM_JP'
    },
    /**
     * Enumeration for the report type.
     *
     * @property {string} ROAD Road
     * @property {string} MILEAGE Mileage
     * @property {string} DIRECTIONS Directions
     * @property {string} DETAIL Detail
     * @property {string} STATE State
     * @property {string} TOLL Toll Detail
     *
     */
    ReportType: {
        ROAD: 'Road',
        MILEAGE: 'Mileage',
        DIRECTIONS: 'Directions',
        DETAIL: 'Detail',
        STATE: 'State',
        TOLL: 'TollDetail'
    },

    Language: {
        AR: 'ar',
        BE: 'be',
        BG: 'bg',
        BN: 'bn',
        BS: 'bs',
        CA: 'ca',
        CS: 'cs',
        CY: 'cy',
        DA: 'da',
        DE: 'de',
        EL: 'el',
        EN: 'en',
        EN_AU: 'en-AU',
        EN_GB: 'en-GB',
        ES: 'es',
        ES_LA: 'es-LA',
        ET: 'et',
        EU: 'eu',
        FI: 'fi',
        FR: 'fr',
        GL: 'gl',
        HE: 'he',
        HI: 'hi',
        HR: 'hr',
        HU: 'hu',
        ID: 'id',
        IS: 'is',
        IT: 'it',
        JA: 'ja',
        KO: 'ko',
        LT: 'lt',
        LV: 'lv',
        MK: 'mk',
        MS: 'ms',
        MT: 'mt',
        NL: 'nl',
        NL_BE: 'nl-BE',
        NO: 'no',
        PL: 'pl',
        PT: 'pt',
        PT_BR: 'pt-BR',
        RO: 'ro',
        RU: 'ru',
        SK: 'sk',
        SL: 'sl',
        SQ: 'sq',
        SR: 'sr',
        SV: 'sv',
        TH: 'th',
        TR: 'tr',
        UK: 'uk',
        VI: 'vi',
        ZH: 'zh',
        ZH_HK: 'zh-HK',
        ZH_HANS: 'zh-Hans'
    },
    /**
     * Enumeration for trailer type.
     *
     * @property {string} NONE None
     * @property {string} CARAVAN Caravan
     * @property {string} TRAILER Trailer
     */
    TrailerType: {
        NONE: 1,
        CARAVAN: 2,
        TRAILER: 3
    },
    /**
     * Enumeration for emission type of the engine.
     *
     * @property {number} EURO0 Euro0
     * @property {number} EUROI EuroI
     * @property {number} EUROII EuroII
     * @property {number} EUROIII EuroIII
     * @property {number} EUROIV EuroIV
     * @property {number} EUROV EuroV
     * @property {number} EUROVI EuroVI
     * @property {number} EUROEEV EuroEEV
     */
    EmissionType: {
        EURO0: 2,
        EUROI: 3,
        EUROII: 4,
        EUROIII: 5,
        EUROIV: 6,
        EUROV: 7,
        EUROVI: 8,
        EUROEEV: 9
    },
    /**
     * HTTP request method.
     *
     * @property {string} GET Get
     * @property {string} POST Post
     */
    RequestMethod: {
        GET: 'GET',
        POST: 'POST'
    },
    /**
     * Estimated arrival and depart type
     * @property {number} ARRIVAL Arrival
     * @property {number} DEPART Depart
     */
    ETAETDType: {
        ARRIVAL: 0,
        DEPART: 1
    },
    /**
     * Date option
     * @property {number} CURRENT Current
     * @property {number} SPECIFIC Specific
     * @property {number} DAY_OF_WEEK Day of week
     */
    DateOption: {
        CURRENT: 0,
        SPECIFIC: 1,
        DAY_OF_WEEK: 2
    },
    /**
     *  Day of week option
     * @property {number} SUNDAY Sunday
     * @property {number} MONDAY Monday
     * @property {number} TUESDAY Tuesday
     * @property {number} WEDNESDAY Wednesday
     * @property {number} THURSDAY Thursday
     * @property {number} FRIDAY Friday
     * @property {number} SATURDAY Saturday
     */
    DayOfWeek: {
        SUNDAY: 0,
        MONDAY: 1,
        TUESDAY: 2,
        WEDNESDAY: 3,
        THURSDAY: 4,
        FRIDAY: 5,
        SATURDAY: 6
    },
    /**
     * Time zone option
     * @property {number} LOCAL Local
     * @property {number} SYSTEM System
     * @property {number} HAWAII Hawaii
     * @property {number} ALASKA Alaska
     * @property {number} PACIFIC Pacific
     * @property {number} ARIZONA Arizona
     * @property {number} MOUNTAIN Mountain
     * @property {number} CENTRAL Central
     * @property {number} EASTERN Eastern
     * @property {number} ATLANTIC Atlantic
     * @property {number} NEWFOUNDLAND Newfoundland
     * @property {number} GMT GMT Greenwich Mean Time
     * @property {number} UTC Coordinated Universal Time
     */
    TimeZone: {
        LOCAL: 0,
        SYSTEM: 1,
        HAWAII: 2,
        ALASKA: 3,
        PACIFIC: 4,
        ARIZONA: 5,
        MOUNTAIN: 6,
        CENTRAL: 7,
        EASTERN: 8,
        ATLANTIC: 9,
        NEWFOUNDLAND: 10,
        GMT: 11,
        UTC: 12
    },
    /**
     * Data region option for derived route and report
     * @property {number} UNKNOWN Unknown
     * @property {number} AF Africa
     * @property {number} AS Asia
     * @property {number} EU Europe
     * @property {number} NA North America
     * @property {number} OC Australia
     * @property {number} SA South America
     * @property {number} ME Middle East
     * @property {number} MX Mexico
     */
    DataRegion: {
        UNKNOWN: 0,
        AF: 1,
        AS: 2,
        EU: 3,
        NA: 4,
        OC: 5,
        SA: 6,
        ME: 7,
        MX: 8
    },

    /**
     * Toll roads type
     * @property {number} ALWAYS_AVOID Always Avoid
     * @property {number} AVOID_IF_POSSIBLE Avoid If Possible
     * @property {number} USE Use
     */
    TollRoadsType: {
        ALWAYS_AVOID: 1,
        AVOID_IF_POSSIBLE: 2,
        USE: 3
    },

    /**
     * Represents the EU Tunnel categories. Applicable for EU region only.
     * @property {number} NONE Indicates that there is no Tunnel category.
     * @property {number} BCDE Indicates EU Tunnel category BCDE.
     * @property {number} CDE Indicates EU Tunnel category CDE.
     * @property {number} DE Indicates EU Tunnel category DE.
     * @property {number} E Indicates EU Tunnel category E.
     */
    TunnelCategory: {
        NONE: 0,
        BCDE: 1,
        CDE: 2,
        DE: 3,
        E: 4
    },

    /**
     * Fuel type
     * @property {number} PETROL Petrol
     * @property {number} PETROL_E5 5% ethanol by vol
     * @property {number} PETROL_E10 10% ethanol by vol
     * @property {number} ETHANOL Ethanol
     * @property {number} DIESEL Diesel
     * @property {number} BIODIESEL Biodiesel
     * @property {number} DIESEL_D5 5% biodiesel by vol
     * @property {number} DIESEL_D7 7% biodiesel by vol
     * @property {number} COUNT Count
     *
     */
    FuelType: {
        PETROL: 0,
        PETROL_E5: 1,
        PETROL_E10: 2,
        ETHANOL: 3,
        DIESEL: 4,
        BIODIESEL: 5,
        DIESEL_D5: 6,
        DIESEL_D7: 7,
        COUNT: 8
    },

    /**
     * Side of street adherence level
     * @property {number} OFF Will not reroute according to the side of the street.
     * @property {number} MINIMAL Adds a cost of a quarter of a mile to ending up on the wrong side of the street.
     * @property {number} MODERATE Adds a cost of half a mile to ending up on the wrong side of the street.
     * @property {number} AVERAGE Adds a cost of one mile to ending up on the wrong side of the street.
     * @property {number} STRICT Adds a cost of five miles to ending up on the wrong side of the street.
     * @property {number} ADHERE Adds a cost of 10 miles to ending up on the wrong side of the street.
     * @property {number} STRONGLY_ADHERE Adds a cost of 1,000 miles to ending up on the wrong side of the street.
     */
    SideOfStreetAdherenceLevel: {
        OFF: 0,
        MINIMAL: 1,
        MODERATE: 2,
        AVERAGE: 3,
        STRICT: 4,
        ADHERE: 5,
        STRONGLY_ADHERE: 6
    },

    /**
     * Represents which Hours Of Service rule to use
     * @property {number} US_FED_607_LH Use US Federal Long Haul 60/7 rule
     * @property {number} US_FED_708_LH Use US Federal Long Haul 70/8 rule
     * @property {number} CANADA_CYCLE_1 Use Canada Cycle 1 rule
     * @property {number} CANADA_CYCLE_2 Use Canada Cycle 2 rule
     * @property {number} CALIFORNIA_808 Use California Intrastate rule
     * @property {number} TEXAS_707 Use Texas Intrastate rule
     * @property {number} US_SHORT_HAUL Use Short Haul Exemption
     * @property {number} EUROPE Use European 48 hour rule
     * @property {number} UNKNOWN Unknown or uninitialized schedule type
     */
    HoSScheduleType: {
        US_FED_607_LH: 0,
        US_FED_708_LH: 1,
        CANADA_CYCLE_1: 2,
        CANADA_CYCLE_2: 3,
        CALIFORNIA_808: 4,
        TEXAS_707: 5,
        US_SHORT_HAUL: 6,
        EUROPE: 7,
        UNKNOWN: 8
    },

    /**
     * Display truck restrictions
     * @property {number} ALWAYS_OFF Always Off
     * @property {number} ALWAYS_ON Always On
     * @property {number} BASED_ON_TRUCK_RESTRICTIONS Based on Truck Restrictions
     */
    DisplayRestrictionsType: {
        ALWAYS_OFF: 0,
        ALWAYS_ON: 1,
        BASED_ON_TRUCK_RESTRICTIONS: 2
    },

    /**
     * Represents the Euro Zone values
     * @property {number} ALLOW Allow
     * @property {number} WARN Warn
     * @property {number} AVOID Avoid
     */
    ZoneType: {
        ALLOW: 0,
        WARN: 1,
        AVOID: 2
    },

    /**
     * Represents the type of roads for road speeds.
     * @property {number} INTER_STATE_RURAL Indicates a rural interstate road type.
     * @property {number} DIVIDED_RURAL Indicates rural divided road type.
     * @property {number} PRIMARY_RURAL Indicates a rural primary road type.
     * @property {number} FERRIES_RURAL Indicates a rural ferry road type.
     * @property {number} SECONDARY_RURAL Indicates a rural secondary road type.
     * @property {number} RAMP_RURAL Indicates a rural ramp road type.
     * @property {number} LOCAL_RURAL Indicates a rural local road type.
     * @property {number} INTER_STATE_URBAN Indicates an urban interstate road type.
     * @property {number} DIVIDED_URBAN Indicates an urban divided road type.
     * @property {number} DIVIDED_URBAN Indicates an urban primary road type.
     * @property {number} FERRIES_URBAN Indicates an urban ferry road type.
     * @property {number} SECONDARY_URBAN Indicates an urban secondary road type.
     * @property {number} RAMP_URBAN Indicates an urban ramp road type.
     * @property {number} LOCAL_URBAN Indicates an urban local road type.
     */
    RoadType: {
        // Thereis no 0
        INTER_STATE_RURAL: 1,
        // There is no 2
        DIVIDED_RURAL: 3,
        PRIMARY_RURAL: 4,
        FERRIES_RURAL: 5,
        SECONDARY_RURAL: 6,
        RAMP_RURAL: 7,
        LOCAL_RURAL: 8,
        // There is no 9
        INTER_STATE_URBAN: 10,
        // There is no 11
        DIVIDED_URBAN: 12,
        PRIMARY_URBAN: 13,
        FERRIES_URBAN: 14,
        SECONDARY_URBAN: 15,
        RAMP_URBAN: 16,
        LOCAL_URBAN: 17
    },

    // *******************************************************************************
    // Rail section
    //
    /**
     * Format for rail freight stations.
     *
     * @property {string} STATION_STATE There are three parts that make up the StationState. Station name, space, and State abbreviation. StationState is not case sensitive. Examples for Oakland, California: Oakland CA
     * @property {string} SPLC Standard Point Location Code. 9 digit geographic code used by North American transportation industries. PC*MILER|Rail will only use the first 6 digits. Example for Boston, Massachusetts: 142000
     * @property {string} ERPC Eastern Railroad President's Conference Codes also referred to as 3-3-3. There are three parts that make up the ERPC. Station name, space, and State abbreviation. ERPC is not case sensitive. Examples for Cabazon, California: Cabazon CA
     * @property {string} FSAC Freight Station Accounting Code. FSAC is a 5 digit code with leading zeros. Examples: 11121
     * @property {string} R260 Rule 260 Junction Code. R260 is 5 characters long and is not case sensitive. Examples: PACIF
     */
    RailStationFormat: {
        STATION_STATE: 'StationState',
        SPLC: 'SPLC',
        ERPC: 'ERPC',
        FSAC: 'FSAC',
        R260: 'R260'
    },
    /**
     * Route calculation heuristic.
     *
     * @property {string} PRACTICAL Practical routing is the optimal for general merchandise traffic.
     * @property {string} INTERMODAL Intermodal routing is specific to intermodal trains. Only locations with intermodal service can be entered as origin/destinations.
     * @property {string} SHORTEST Shortest routing will route to the shortest path.
     * @property {string} COAL_BULK Coal/Bulk Routing is specific to Coal/Bulk unit trains.
     * @property {string} AUTO_RACKS AutoRack routing is specific to multi-levels.
     * @property {string} FUEL_SURCHARGE Fuel Surcharge routing is practical or shortest routing depending on the railroad.
     */
    RailRoutePreference: {
        PRACTICAL: 'Practical',
        INTERMODAL: 'Intermodal',
        SHORTEST: 'Shortest',
        COAL_BULK: 'CoalBulk',
        AUTO_RACKS: 'AutoRacks',
        FUEL_SURCHARGE: 'FuelSurcharge'
    },
    /**
     * Specifies the unit system when displaying distance results.
     *
     * @property {string} MILES Indicates miles should be used.
     * @property {string} KILOMETERS Indicates kilometers should be used.
     */
    RailDistanceUnit: {
        MILES: 'Miles',
        KILOMETERS: 'Kilometers'
    },

    /**
     * Used to indicate when drag routing will snap to visible roads.
     *
     * @property {number} OFF Snapping is disabled.
     * @property {number} ENABLE_WITH_KEY Snapping is only enabled when defined key is pressed.
     * @property {number} DISABLE_WITH_KEY Snapping is enabled and is disabled when defined key is pressed.
     */
    SnapMode: {
        OFF: 0,
        ENABLE_WITH_KEY: 1,
        DISABLE_WITH_KEY: 2
    },

    /**
     * The modifier key used by drag routing when snapping to visible roads.
     *
     * @property {string} SHIFT Shift modifier key.
     * @property {string} CONTROL Control modifier key.
     * @property {string} ALT Alt modifier key.
     */
    SnapKey: {
        SHIFT: 'shiftKey',
        CONTROL: 'ctrlKey',
        ALT: 'altKey'
    },

    /**
     * The modifier key used by removing via point flag introduced by drag routing.
     *
     * @property {string} ALT Alt modifier key.
     * @property {string} CONTROL Control modifier key.
     * @property {string} SHIFT Shift modifier key.
     */
    ViaPointModifierKey: {
        ALT: 'altKey',
        CONTROL: 'ctrlKey',
        SHIFT: 'shiftKey'
    },

    /**
     * Enumeration for the Satellite Provider.
     *
     * @property {string} DEFAULT satellite source provider for default DigitalGlobeAPIImagery
     * @property {string} SAT1 DigitalGlobe
     * @property {string} SAT2 DigitalGlobeConsumer
     * @property {string} SAT3 DigitalGlobeTrueCurrency
     * @property {string} SAT4 DigitalGlobeColorConsumer
     * @property {string} SAT5 DigitalGlobeAPIVivid
     * @property {string} SAT6 DigitalGlobeAPIRecent
     * @property {string} ALKCURATED ALKCurated certain curated data is excluded.
     *
     */
    SatelliteProvider: {
        DEFAULT: 'default',
        SAT1: 'sat1',
        SAT2: 'sat2',
        SAT3: 'sat3',
        SAT4: 'sat4',
        SAT5: 'sat5',
        SAT6: 'sat6',
        ALKCURATED: 'alkcurated'

    }
};

export type StyleEnum = $Values<typeof Common.Style>;
export type RegionEnum = $Values<typeof Common.Region>;
export type VehicleTypeEnum = $Values<typeof Common.VehicleType>;
export type RouteTypeEnum = $Values<typeof Common.RouteType>;
export type RouteOptimizationEnum = $Values<typeof Common.RouteOptimization>;
export type ClassOverrideEnum = $Values<typeof Common.ClassOverride>;
export type HazMatTypeEnum = $Values<typeof Common.HazMatType>;
export type UnitEnum = $Values<typeof Common.Unit>;
export type DistanceUnitEnum = $Values<typeof Common.DistanceUnit>;
export type TruckUnitEnum = $Values<typeof Common.TruckUnit>;
export type FuelUnitEnum = $Values<typeof Common.FuelUnit>;
export type TruckConfigEnum = $Values<typeof Common.TruckConfig>;
export type TollCurrencyEnum = $Values<typeof Common.TollCurrency>;
export type DataVersionEnum = $Values<typeof Common.DataVersion>;
export type ReportTypeEnum = $Values<typeof Common.ReportType>;
export type TrailerTypeEnum = $Values<typeof Common.TrailerType>;
export type EmissionTypeEnum = $Values<typeof Common.EmissionType>;
export type RequestMethodEnum = $Values<typeof Common.RequestMethod>;
export type ETAETDTypeEnum = $Values<typeof Common.ETAETDType>;
export type DateOptionEnum = $Values<typeof Common.DateOption>;
export type DayOfWeekEnum = $Values<typeof Common.DayOfWeek>;
export type TimeZoneEnum = $Values<typeof Common.TimeZone>;
export type DataRegionEnum = $Values<typeof Common.DataRegion>;
export type TollRoadsTypeEnum = $Values<typeof Common.TollRoadsType>;
export type TunnelCategoryEnum = $Values<typeof Common.TunnelCategory>;
export type FuelTypeEnum = $Values<typeof Common.FuelType>;
export type SideOfStreetAdherenceEnum = $Values<typeof Common.SideOfStreetAdherenceLevel>;
export type HoSScheduleTypeEnum = $Values<typeof Common.HoSScheduleType>;
export type DisplayRestrictionsTypeEnum = $Values<typeof Common.DisplayRestrictionsType>;
export type ZoneTypeEnum = $Values<typeof Common.ZoneType>;
export type RoadTypeEnum = $Values<typeof Common.RoadType>;
export type RailStationFormatEnum = $Values<typeof Common.RailStationFormat>;
export type RailRoutePreferenceEnum = $Values<typeof Common.RailRoutePreference>;
export type RailDistanceUnitEnum = $Values<typeof Common.RailDistanceUnit>;
export type SatelliteProviderEnum = $Values<typeof Common.SatelliteProvider>;
export type SnapMode = $Values<typeof Common.SnapMode>;
export type SnapKey = $Values<typeof Common.SnapKey>;
export type ViaPointModifierKey = $Values<typeof Common.ViaPointModifierKey>;
